// Paint layer for the free calculators' frequency-magnitude charts (SBIR
// null, comb-filter, modal seat response) — the tool-facing equivalent of
// chartDraw.ts's drawFreqResponse, but for an arbitrary number of curves, an
// optional per-curve "relative" mean-center, mode-resonance tick lines and a
// single read-only marker line. Geometry (scales, ticks, per-curve dB/point
// math) comes from `computeToolFreqGeometry` in @roomtreatment/charts; this
// file owns only the ctx calls, exactly per AGENTS.md's chart/plot rule.
import { canvasTypeFont, withAlpha, type DesignToken } from '@roomtreatment/design';
import {
  computeToolFreqGeometry,
  DEFAULT_CHART_PAD,
  linearToPlot,
  logFreqToX,
  type ChartPad,
  type ToolFreqCurveUnit,
} from '@roomtreatment/charts';
import { tokensAt } from '@/lib/theme/tokens';
import {
  chartGridInk,
  drawLogFreqAxisGrid,
  fitYAxisPad,
  setupChartCanvas,
  EMPTY_PAINT,
  type ChartPaint,
} from '@/components/room/charts/chartCanvas';

/** The shared default with a left edge that holds a "-24" dB tick beside the
 *  rotated unit at the canvas tick font; `fitYAxisPad` widens it further for
 *  a wider set. */
export const PAD_TOOL_FREQ: ChartPad = { ...DEFAULT_CHART_PAD, l: 44 };

/**
 * What a curve IS, not what colour it is. The chart turns the role into a
 * concrete colour at draw time from the canvas's own theme — a caller cannot
 * hand it a colour string, because the obvious one (`'var(--rt-accent)'`) is
 * silently ignored by `ctx.strokeStyle` and a hex captured at import is frozen
 * in the light theme.
 */
export type CurveTone =
  /** The curve the tool is about: the in-room / at-seat / with-SBIR response. */
  | 'accent'
  /** The reference it is compared against (the pure, anechoic response). */
  | 'reference';

export function curveStroke(tone: CurveTone, t: Record<DesignToken, string>): string {
  return tone === 'accent' ? t.accent : withAlpha(t.ink, 0.38);
}

export interface ModeTick {
  hz: number;
  /** Stronger tick for axial modes. */
  emphasis?: 'axial' | 'tangential' | 'oblique' | string;
}

export interface ToolFreqCurve {
  /** Linear magnitude, or dB if `unit` is `'db'`. */
  mag: ArrayLike<number>;
  tone: CurveTone;
  unit?: ToolFreqCurveUnit;
}

export interface ToolFreqChartOpts {
  freqs: number[];
  curves: ToolFreqCurve[];
  /** Optional marker frequency (Hz) with label — a fixed, read-only
   *  annotation (the selected mode, the primary SBIR null), not a draggable
   *  scrubber: none of these tools have a second piece of state for a chart
   *  drag to write back to. */
  markerHz?: number;
  markerLabel?: string;
  modeTicks?: ModeTick[];
  fMin?: number;
  fMax?: number;
  relative?: boolean;
  dbRange?: { min: number; max: number };
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  frame: { plotX: number; plotY: number },
  pts: { x: number; y: number }[],
  color: string,
  width: number,
) {
  if (pts.length < 2) return;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(frame.plotX + pts[0]!.x, frame.plotY + pts[0]!.y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(frame.plotX + pts[i]!.x, frame.plotY + pts[i]!.y);
  ctx.stroke();
}

export function drawToolFreq(canvas: HTMLCanvasElement, chart: ToolFreqChartOpts): ChartPaint {
  const s = setupChartCanvas(canvas);
  if (!s) return EMPTY_PAINT;
  const { ctx, W } = s;
  if (!chart.freqs.length || !chart.curves.length) return EMPTY_PAINT;

  const t = tokensAt(canvas);

  const geo = fitYAxisPad(ctx, PAD_TOOL_FREQ, (pad) => computeToolFreqGeometry({
    freqs: chart.freqs,
    curves: chart.curves.map((c) => ({ values: Array.from(c.mag), unit: c.unit })),
    fMin: chart.fMin,
    fMax: chart.fMax,
    relative: chart.relative,
    dbRange: chart.dbRange,
    width: W,
    height: s.H,
    pad,
    measureWidth: (label) => ctx.measureText(label).width,
  }), (g) => ({ left: g.yTicks.map((t) => t.label) }));
  if (geo.empty) return EMPTY_PAINT;
  const { frame } = geo;

  // Mode ticks, behind the grid: vertical lines at each room resonance,
  // heavier for axial modes, heaviest when it matches the marker frequency.
  if (chart.modeTicks?.length) {
    for (const tick of chart.modeTicks) {
      if (tick.hz < geo.fMin || tick.hz > geo.fMax) continue;
      const axial = tick.emphasis === 'axial';
      const highlighted = chart.markerHz != null && Math.abs(tick.hz - chart.markerHz) < 0.05;
      ctx.strokeStyle = highlighted
        ? withAlpha(t.accent, 0.55)
        : axial
          ? withAlpha(t.ink, 0.16)
          : withAlpha(t.ink, 0.07);
      ctx.lineWidth = highlighted ? 1.5 : 1;
      const x = frame.plotX + logFreqToX(geo.xScale, tick.hz);
      ctx.beginPath();
      ctx.moveTo(x, frame.plotY);
      ctx.lineTo(x, frame.plotY + frame.plotH);
      ctx.stroke();
    }
  }

  const { gridMajor, gridMinor } = chartGridInk(t);
  drawLogFreqAxisGrid(ctx, frame, geo.xTicks, geo.yTicks, { gridMajor, gridMinor, xLabel: 'Hz', yLabel: 'dB' });

  // 0 dB reference: only means something when the axis is asking "how far
  // from flat", i.e. a relative curve or a caller-fixed dB window.
  if ((chart.relative || chart.dbRange) && geo.yDomain.lo < 0 && geo.yDomain.hi > 0) {
    ctx.strokeStyle = withAlpha(t.ink, 0.2);
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    const y = frame.plotY + linearToPlot(geo.yScale, 0);
    ctx.beginPath();
    ctx.moveTo(frame.plotX, y);
    ctx.lineTo(frame.plotX + frame.plotW, y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  chart.curves.forEach((c, i) => {
    drawLine(ctx, frame, geo.curves[i]!.points, curveStroke(c.tone, t), 2);
  });

  if (chart.markerHz != null && chart.markerHz >= geo.fMin && chart.markerHz <= geo.fMax) {
    const x = frame.plotX + logFreqToX(geo.xScale, chart.markerHz);
    ctx.strokeStyle = withAlpha(t.accent, 0.9);
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(x, frame.plotY);
    ctx.lineTo(x, frame.plotY + frame.plotH);
    ctx.stroke();
    ctx.setLineDash([]);
    if (chart.markerLabel) {
      ctx.fillStyle = t.accent;
      ctx.font = canvasTypeFont('label');
      ctx.textAlign = 'left';
      const labelW = ctx.measureText(chart.markerLabel).width;
      ctx.fillText(chart.markerLabel, Math.min(x + 4, W - labelW - 8), frame.plotY + 12);
    }
  }

  return {
    caption: '',
    plot: { frame, x: { scale: geo.xScale }, y: { scale: geo.yScale } },
  };
}
